import type { ValueOf } from './util/typeUtils';

export const CONSORS_API_RETRIES = 30;
export const CONSORS_API_RETRY_INTERVAL = 1000;

export const CONSORS_SUBSCRIPTION_STATUSES = {
    ERROR: 'ERROR',
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
} as const;
export type CONSORS_SUBSCRIPTION_STATUS = ValueOf<typeof CONSORS_SUBSCRIPTION_STATUSES>;

export const CONSORS_FINALIZED_SUBSCRIPTION_STATUSES = {
    ERROR: 'ERROR',
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
} as const;
export type CONSORS_FINALIZED_SUBSCRIPTION_STATUS = ValueOf<typeof CONSORS_FINALIZED_SUBSCRIPTION_STATUSES>;

export const CONSORS_LOAN_OFFERS_REQUEST_STATUSES = {
    ERROR: 'ERROR',
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
} as const;
export type CONSORS_LOAN_OFFERS_REQUEST_STATUS = ValueOf<typeof CONSORS_LOAN_OFFERS_REQUEST_STATUSES>;

export const CONSORS_INSURANCE_OPTIONS = {
    DEATH: 'DEATH',
    DEATH_DISABILITY: 'DEATH_DISABILITY',
    DEATH_DISABILITY_50: 'DEATH_DISABILITY_50',
    DEATH_DISABILITY_UNEMPLOYMENT: 'DEATH_DISABILITY_UNEMPLOYMENT',
    DEATH_DISABILITY_UNEMPLOYMENT_50: 'DEATH_DISABILITY_UNEMPLOYMENT_50',
    DREADDISEASE: 'DREADDISEASE',
    DREADDISEASE_DISABILITY: 'DREADDISEASE_DISABILITY',
    DREADDISEASE_DISABILITY_50: 'DREADDISEASE_DISABILITY_50',
    DREADDISEASE_DISABILITY_UNEMPLOYMENT: 'DREADDISEASE_DISABILITY_UNEMPLOYMENT',
    DREADDISEASE_DISABILITY_UNEMPLOYMENT_50: 'DREADDISEASE_DISABILITY_UNEMPLOYMENT_50',
    NONE: 'NONE',
} as const;
export type CONSORS_INSURANCE_OPTION = ValueOf<typeof CONSORS_INSURANCE_OPTIONS>;

export const CONSORS_REFUSAL_CATEGORIES = {
    CREDIT_WORTHINESS: 1,
    DOCUMENTATION: 2,
    OTHER: 3,
} as const;
export type CONSORS_REFUSAL_CATEGORY = ValueOf<typeof CONSORS_REFUSAL_CATEGORIES>;

export const CONSORS_SUBSCRIPTION_VALIDATION_FAILURE_REASONS = {
    AUTHORISATION_ERROR: 'AUTHORISATION_ERROR',
    CANCELLATION_PROCESS_ALREADY_RUNNING: 'CANCELLATION_PROCESS_ALREADY_RUNNING',
    ERROR_TRYING_TO_MAP_PAYLOAD: 'ERROR_TRYING_TO_MAP_PAYLOAD',
    ERROR_VALIDATING_SUBSCRIPTION: 'ERROR_VALIDATING_SUBSCRIPTION',
    SUBSCRIPTION_ALREADY_CANCELLED: 'SUBSCRIPTION_ALREADY_CANCELLED',
    SUBSCRIPTION_NOT_FOUND: 'SUBSCRIPTION_NOT_FOUND',
    VALIDATION_PROCESS_ALREADY_RUNNING: 'VALIDATION_PROCESS_ALREADY_RUNNING',
} as const;
export type CONSORS_SUBSCRIPTION_VALIDATION_FAILURE_REASON = ValueOf<
    typeof CONSORS_SUBSCRIPTION_VALIDATION_FAILURE_REASONS
>;

// guard rejections of a parallel request: must not overwrite the row of the running process
export const CONSORS_SUBSCRIPTION_VALIDATION_GUARD_FAILURE_REASONS: ReadonlyArray<CONSORS_SUBSCRIPTION_VALIDATION_FAILURE_REASON> =
    [
        CONSORS_SUBSCRIPTION_VALIDATION_FAILURE_REASONS.CANCELLATION_PROCESS_ALREADY_RUNNING,
        CONSORS_SUBSCRIPTION_VALIDATION_FAILURE_REASONS.VALIDATION_PROCESS_ALREADY_RUNNING,
    ];

export const CONSORS_CONSOLIDATION_REQUEST_STATUSES = {
    ERROR: 'ERROR',
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
} as const;
export type CONSORS_CONSOLIDATION_REQUEST_STATUS = ValueOf<typeof CONSORS_CONSOLIDATION_REQUEST_STATUSES>;

export const CONSORS_CONSOLIDATION_ACCESS_KINDS = {
    AVAILABLE: 'AVAILABLE',
    EXPIRED: 'EXPIRED',
    UNAVAILABLE: 'UNAVAILABLE',
} as const;
export type CONSORS_CONSOLIDATION_ACCESS_KIND = ValueOf<typeof CONSORS_CONSOLIDATION_ACCESS_KINDS>;

export const CONSORS_CONSOLIDATION_REQUEST_FAILURE_REASONS = {
    AUTHORISATION_ERROR: 'AUTHORISATION_ERROR',
    CONSOLIDATION_REQUEST_ALREADY_RUNNING: 'CONSOLIDATION_REQUEST_ALREADY_RUNNING',
    CONSOLIDATION_REQUEST_FAILED: 'CONSOLIDATION_REQUEST_FAILED',
    NO_VALID_SUBSCRIPTION: 'NO_VALID_SUBSCRIPTION',
    SUBSCRIPTION_ALREADY_REJECTED: 'SUBSCRIPTION_ALREADY_REJECTED',
} as const;
export type CONSORS_CONSOLIDATION_REQUEST_FAILURE_REASON = ValueOf<
    typeof CONSORS_CONSOLIDATION_REQUEST_FAILURE_REASONS
>;

// `errorCode` of Consors 400 bodies, sent as `<CODE>` or `<CODE> : <contractIdentifier>`
export const CONSORS_API_ERROR_CODES = {
    SUBSCRIPTION_ALREADY_CANCELLED: 'SUBSCRIPTION_ALREADY_CANCELLED',
    SUBSCRIPTION_ALREADY_REJECTED: 'SUBSCRIPTION_ALREADY_REJECTED',
    SUBSCRIPTION_INVALID_REQUEST_PARAMS_ERROR: 'SUBSCRIPTION_INVALID_REQUEST_PARAMS_ERROR',
} as const;
export type CONSORS_API_ERROR_CODE = ValueOf<typeof CONSORS_API_ERROR_CODES>;

export const CONSORS_LOAN_OFFERS_REQUEST_FAILURE_REASONS = {
    AUTHORISATION_ERROR: 'AUTHORISATION_ERROR',
    CREDIT_DECISION_ANALYZING: 'CREDIT_DECISION_ANALYZING',
    CREDIT_DECISION_CANCELLED: 'CREDIT_DECISION_CANCELLED',
    CREDIT_DECISION_REFUSED: 'CREDIT_DECISION_REFUSED',
    CREDIT_DECISION_SCHUFA_ERROR: 'CREDIT_DECISION_SCHUFA_ERROR',
    CREDIT_DECISION_STUDY: 'CREDIT_DECISION_STUDY',
    LOAN_OFFERS_REQUEST_ALREADY_RUNNING: 'LOAN_OFFERS_REQUEST_ALREADY_RUNNING',
    LOAN_OFFERS_REQUEST_FAILED: 'LOAN_OFFERS_REQUEST_FAILED',
    MISSING_CREDIT_STATES: 'MISSING_CREDIT_STATES',
    NO_VALID_CONTRACT: 'NO_VALID_CONTRACT',
    OFFER_NOT_YET_AVAILABLE: 'OFFER_NOT_YET_AVAILABLE',
    OFFER_STATUS_UNKNOWN: 'OFFER_STATUS_UNKNOWN',
} as const;
export type CONSORS_LOAN_OFFERS_REQUEST_FAILURE_REASON = ValueOf<typeof CONSORS_LOAN_OFFERS_REQUEST_FAILURE_REASONS>;

export const CONSORS_ADD_NOTE_FAILURE_REASONS = {
    ADD_NOTE_FAILED: 'ADD_NOTE_FAILED',
    ADD_NOTE_PROCESS_ALREADY_RUNNING: 'ADD_NOTE_PROCESS_ALREADY_RUNNING',
    AUTHORISATION_ERROR: 'AUTHORISATION_ERROR',
    NO_VALID_CONTRACT: 'NO_VALID_CONTRACT',
    NO_VALID_SUBSCRIPTION: 'NO_VALID_SUBSCRIPTION',
} as const;
export type CONSORS_ADD_NOTE_FAILURE_REASON = ValueOf<typeof CONSORS_ADD_NOTE_FAILURE_REASONS>;

export const CONSORS_ADD_NOTE_STATUSES = {
    ERROR: 'ERROR',
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
} as const;
export type CONSORS_ADD_NOTE_STATUS = ValueOf<typeof CONSORS_ADD_NOTE_STATUSES>;

export const CONSORS_NOTE_SOURCES = {
    CONSORS: 'CONSORS',
    CPOOL: 'CPOOL',
} as const;
export type CONSORS_NOTE_SOURCE = ValueOf<typeof CONSORS_NOTE_SOURCES>;

export const CONSORS_NOTES_REQUEST_FAILURE_REASONS = {
    AUTHORISATION_ERROR: 'AUTHORISATION_ERROR',
    NO_VALID_CONTRACT: 'NO_VALID_CONTRACT',
    NO_VALID_SUBSCRIPTION: 'NO_VALID_SUBSCRIPTION',
    NOTES_NOT_FOUND: 'NOTES_NOT_FOUND',
    NOTES_REQUEST_FAILED: 'NOTES_REQUEST_FAILED',
    NOTES_REQUEST_PROCESS_ALREADY_RUNNING: 'NOTES_REQUEST_PROCESS_ALREADY_RUNNING',
} as const;
export type CONSORS_NOTES_REQUEST_FAILURE_REASON = ValueOf<typeof CONSORS_NOTES_REQUEST_FAILURE_REASONS>;

// notes polling every 2 h, 07:00–21:00 German time (no Consors calls at night)
export const CONSORS_NOTES_REQUEST_SCHEDULE = {
    PATTERN: '0 7-21/2 * * *',
    TIMEZONE: 'Europe/Berlin',
} as const;

export const CONSORS_NOTES_REQUEST_STATUSES = {
    ERROR: 'ERROR',
    NO_CHANGE: 'NO_CHANGE',
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
} as const;
export type CONSORS_NOTES_REQUEST_STATUS = ValueOf<typeof CONSORS_NOTES_REQUEST_STATUSES>;

// PENDING request rows older than this no longer block a new request (crashed/lost request)
export const CONSORS_PENDING_REQUEST_STALE_AFTER_MS = 15 * 60 * 1000;

export const CONSORS_CANCEL_SUBSCRIPTION_STATUSES = {
    ERROR: 'ERROR',
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
} as const;
export type CONSORS_CANCEL_SUBSCRIPTION_STATUS = ValueOf<typeof CONSORS_CANCEL_SUBSCRIPTION_STATUSES>;

export const CONSORS_SUBSCRIPTION_CANCELLATION_FAILURE_REASONS = {
    CANCELLATION_REQUEST_FAILED: 'CANCELLATION_REQUEST_FAILED',
    SUBSCRIPTION_ALREADY_CANCELLED: 'SUBSCRIPTION_ALREADY_CANCELLED',
} as const;
export type CONSORS_SUBSCRIPTION_CANCELLATION_FAILURE_REASON = ValueOf<
    typeof CONSORS_SUBSCRIPTION_CANCELLATION_FAILURE_REASONS
>;

export const CONSORS_OFFER_DECISION_COLORS = {
    GRAY: 'Gray',
    GREEN: 'Green',
    RED: 'Red',
    YELLOW: 'Yellow',
} as const;
export type CONSORS_OFFER_DECISION_COLOR = ValueOf<typeof CONSORS_OFFER_DECISION_COLORS>;

export const CONSORS_CUSTOMER_ADVERTISEMENT_CONSENTS = {
    ALL_PHONE_EMAIL_POST: 'ALL_PHONE_EMAIL_POST',
    EMAIL: 'EMAIL',
    EMAIL_LM: 'EMAIL_LM',
    KNOWN_CUSTOMER_DO_NOT_OVERRIDE: 'KNOWN_CUSTOMER_DO_NOT_OVERRIDE',
    NONE: 'NONE',
    NOTHING_SELECTED: 'NOTHING_SELECTED',
    ONLY_SMS: 'ONLY_SMS',
    POST: 'POST',
    POST_EMAIL: 'POST_EMAIL',
    POST_EMAIL_SELL_CONTACT: 'POST_EMAIL_SELL_CONTACT',
    POST_EMAIL_TELEPHONE_SELL_CONTACT: 'POST_EMAIL_TELEPHONE_SELL_CONTACT',
    POST_EMAIL_TELEPHONE_SELL_EXISTING_CONTACT: 'POST_EMAIL_TELEPHONE_SELL_EXISTING_CONTACT',
    SMS_EMAIL: 'SMS_EMAIL',
    TELEPHONE: 'TELEPHONE',
    TELEPHONE_EMAIL: 'TELEPHONE_EMAIL',
    TELEPHONE_EMAIL_SMS: 'TELEPHONE_EMAIL_SMS',
    TELEPHONE_EMAIL_SMS_AND_3RD_PARTY: 'TELEPHONE_EMAIL_SMS_AND_3RD_PARTY',
    TELEPHONE_LM: 'TELEPHONE_LM',
    TELEPHONE_SMS: 'TELEPHONE_SMS',
    VENDOR_ONLY: 'VENDOR_ONLY',
} as const;
export type CONSORS_CUSTOMER_ADVERTISEMENT_CONSENT = ValueOf<typeof CONSORS_CUSTOMER_ADVERTISEMENT_CONSENTS>;

export const CONSORS_ACADEMIC_TITLES = {
    DR: 'DR',
    DR_DR: 'DR_DR',
    DR_DR_DR: 'DR_DR_DR',
    DR_DR_EH: 'DR_DR_EH',
    DR_DR_HC: 'DR_DR_HC',
    DR_EH: 'DR_EH',
    DR_HC: 'DR_HC',
    DR_ING: 'DR_ING',
    DR_JUR: 'DR_JUR',
    DR_MED: 'DR_MED',
    DR_MED_DENT: 'DR_MED_DENT',
    DR_MED_VET: 'DR_MED_VET',
    DR_PHIL: 'DR_PHIL',
    DR_RER_NAT: 'DR_RER_NAT',
    DR_RER_POL: 'DR_RER_POL',
    PROF: 'PROF',
    PROF_DR: 'PROF_DR',
    PROF_DR_DR: 'PROF_DR_DR',
    PROF_DR_MED: 'PROF_DR_MED',
    SENATOR_EH: 'SENATOR_EH',
} as const;
export type CONSORS_ACADEMIC_TITLE = ValueOf<typeof CONSORS_ACADEMIC_TITLES>;

export const CONSORS_CONSORSCUSTOMERADVERTISMENTCONSENTS = {
    ALL_PHONE_EMAIL_POST: 'ALL_PHONE_EMAIL_POST',
    EMAIL: 'EMAIL',
    EMAIL_LM: 'EMAIL_LM',
    KNOWN_CUSTOMER_DO_NOT_OVERRIDE: 'KNOWN_CUSTOMER_DO_NOT_OVERRIDE',
    NONE: 'NONE',
    NOTHING_SELECTED: 'NOTHING_SELECTED',
    ONLY_SMS: 'ONLY_SMS',
    POST: 'POST',
    POST_EMAIL: 'POST_EMAIL',
    POST_EMAIL_SELL_CONTACT: 'POST_EMAIL_SELL_CONTACT',
    POST_EMAIL_TELEPHONE_SELL_CONTACT: 'POST_EMAIL_TELEPHONE_SELL_CONTACT',
    POST_EMAIL_TELEPHONE_SELL_EXISTING_CONTACT: 'POST_EMAIL_TELEPHONE_SELL_EXISTING_CONTACT',
    SMS_EMAIL: 'SMS_EMAIL',
    TELEPHONE: 'TELEPHONE',
    TELEPHONE_EMAIL: 'TELEPHONE_EMAIL',
    TELEPHONE_EMAIL_SMS: 'TELEPHONE_EMAIL_SMS',
    TELEPHONE_EMAIL_SMS_AND_3RD_PARTY: 'TELEPHONE_EMAIL_SMS_AND_3RD_PARTY',
    TELEPHONE_LM: 'TELEPHONE_LM',
    TELEPHONE_SMS: 'TELEPHONE_SMS',
    VENDOR_ONLY: 'VENDOR_ONLY',
} as const;
export type CONSORS_CONSORSCUSTOMERADVERTISMENTCONSENT = ValueOf<typeof CONSORS_CONSORSCUSTOMERADVERTISMENTCONSENTS>;

export const CONSORS_FAMILY_STATUSES = {
    COHABIT: 'COHABIT',
    DIVORCED: 'DIVORCED',
    FREE: 'FREE',
    MARRIED: 'MARRIED',
    SEPARATED: 'SEPARATED',
    WIDOWED: 'WIDOWED',
} as const;
export type CONSORS_FAMILY_STATUS = ValueOf<typeof CONSORS_FAMILY_STATUSES>;

export const CONSORS_GENDERS = {
    FEMALE: 'FEMALE',
    MALE: 'MALE',
};
export type CONSORS_GENDER = ValueOf<typeof CONSORS_GENDERS>;

export const CONSORS_HOUSING_SITUATIONS = {
    BY_FAMILY: 'BY_FAMILY',
    EMPLOYER: 'EMPLOYER',
    OTHER: 'OTHER',
    OWNER_WITH_MORTGAGE: 'OWNER_WITH_MORTGAGE',
    OWNER_WITHOUT_MORTGAGE: 'OWNER_WITHOUT_MORTGAGE',
    RENTER: 'RENTER',
} as const;
export type CONSORS_HOUSING_SITUATION = ValueOf<typeof CONSORS_HOUSING_SITUATIONS>;

export const CONSORS_IDENTIFICATION_TYPES = {
    EU_ID_CARD: 'EU_ID_CARD',
    EU_PASS: 'EU_PASS',
    FOREIGN_PASS_PERMISION: 'FOREIGN_PASS_PERMISION',
    GERMAN_ID_CARD: 'GERMAN_ID_CARD',
    GERMAN_ID_CARD_TMP: 'GERMAN_ID_CARD_TMP',
    GERMAN_PASS: 'GERMAN_PASS',
    NO_PASS: 'NO_PASS',
} as const;
export type CONSORS_IDENTIFICATION_TYPE = ValueOf<typeof CONSORS_IDENTIFICATION_TYPES>;

export const CONSORS_INDUSTRIES = {
    AGRICULTURE_FORESTRY_FISHING_FISH_FARMING: 'AGRICULTURE_FORESTRY_FISHING_FISH_FARMING',
    CONSTRUCTION: 'CONSTRUCTION',
    FINANCIAL_INSTITUTIONS__INSURANCE_COMPANIES: 'FINANCIAL_INSTITUTIONS__INSURANCE_COMPANIES',
    MANUFACTURING: 'MANUFACTURING',
    MINING_QUARRYING_EARTH: 'MINING_QUARRYING_EARTH',
    OTHER: 'OTHER',
    SERVICES_INCLUDING_FREELANCE_PROFESSIONS: 'SERVICES_INCLUDING_FREELANCE_PROFESSIONS',
    TRADE_SERVICE_REPAIR_VEHICLES_CONSUMER_GOODS: 'TRADE_SERVICE_REPAIR_VEHICLES_CONSUMER_GOODS',
    TRAFFIC_MESSAGE_DELIVERY: 'TRAFFIC_MESSAGE_DELIVERY',
} as const;
export type CONSORS_INDUSTRY = ValueOf<typeof CONSORS_INDUSTRIES>;

export const CONSORS_KYC_STATUSES = {
    FAILED: 'FAILED',
    HIGH_RISK: 'HIGH_RISK',
    LOW_RISK: 'LOW_RISK',
    MEDIUM_RISK: 'MEDIUM_RISK',
    NOT_AVAILABLE: 'NOT_AVAILABLE',
} as const;
export type CONSORS_KYC_STATUS = ValueOf<typeof CONSORS_KYC_STATUSES>;

export const CONSORS_NATIONALITY_GERMANY = {
    '276': '276',
} as const;

export const CONSORS_NATIONALITY_AUSTRIA = {
    '040': '040',
} as const;

export const CONSORS_NATIONALITY_ISO_EU = {
    '056': '056',
    '100': '100',
    '191': '191',
    '196': '196',
    '203': '203',
    '208': '208',
    '233': '233',
    '246': '246',
    '250': '250',
    '300': '300',
    '348': '348',
    '372': '372',
    '380': '380',
    '428': '428',
    '440': '440',
    '442': '442',
    '470': '470',
    '528': '528',
    '616': '616',
    '620': '620',
    '642': '642',
    '703': '703',
    '705': '705',
    '724': '724',
    '752': '752',
    '826': '826',
} as const;
export type CONSORS_NATIONALITY_ISO_EU_TYPE = ValueOf<typeof CONSORS_NATIONALITY_ISO_EU>;

export const CONSORS_NATIONALITY_ISO_OTHERS = {
    '004': '004',
    '008': '008',
    '010': '010',
    '012': '012',
    '016': '016',
    '020': '020',
    '024': '024',
    '028': '028',
    '031': '031',
    '032': '032',
    '036': '036',
    '044': '044',
    '048': '048',
    '050': '050',
    '051': '051',
    '052': '052',
    '060': '060',
    '064': '064',
    '068': '068',
    '070': '070',
    '072': '072',
    '074': '074',
    '076': '076',
    '084': '084',
    '086': '086',
    '092': '092',
    '096': '096',
    '104': '104',
    '108': '108',
    '112': '112',
    '116': '116',
    '120': '120',
    '124': '124',
    '132': '132',
    '136': '136',
    '140': '140',
    '144': '144',
    '148': '148',
    '152': '152',
    '156': '156',
    '158': '158',
    '162': '162',
    '166': '166',
    '170': '170',
    '175': '175',
    '178': '178',
    '180': '180',
    '184': '184',
    '188': '188',
    '192': '192',
    '204': '204',
    '212': '212',
    '214': '214',
    '218': '218',
    '222': '222',
    '226': '226',
    '231': '231',
    '232': '232',
    '234': '234',
    '238': '238',
    '239': '239',
    '242': '242',
    '258': '258',
    '260': '260',
    '262': '262',
    '266': '266',
    '268': '268',
    '270': '270',
    '275': '275',
    '288': '288',
    '292': '292',
    '296': '296',
    '304': '304',
    '308': '308',
    '316': '316',
    '320': '320',
    '324': '324',
    '328': '328',
    '332': '332',
    '334': '334',
    '336': '336',
    '340': '340',
    '352': '352',
    '356': '356',
    '360': '360',
    '364': '364',
    '368': '368',
    '376': '376',
    '384': '384',
    '388': '388',
    '392': '392',
    '398': '398',
    '400': '400',
    '404': '404',
    '408': '408',
    '410': '410',
    '414': '414',
    '417': '417',
    '418': '418',
    '422': '422',
    '426': '426',
    '430': '430',
    '434': '434',
    '438': '438',
    '446': '446',
    '450': '450',
    '454': '454',
    '458': '458',
    '462': '462',
    '466': '466',
    '474': '474',
    '478': '478',
    '480': '480',
    '484': '484',
    '496': '496',
    '498': '498',
    '499': '499',
    '500': '500',
    '504': '504',
    '508': '508',
    '512': '512',
    '516': '516',
    '520': '520',
    '524': '524',
    '533': '533',
    '540': '540',
    '548': '548',
    '554': '554',
    '558': '558',
    '562': '562',
    '566': '566',
    '570': '570',
    '574': '574',
    '578': '578',
    '580': '580',
    '583': '583',
    '584': '584',
    '585': '585',
    '586': '586',
    '591': '591',
    '598': '598',
    '600': '600',
    '604': '604',
    '608': '608',
    '612': '612',
    '624': '624',
    '626': '626',
    '630': '630',
    '634': '634',
    '643': '643',
    '646': '646',
    '654': '654',
    '659': '659',
    '660': '660',
    '662': '662',
    '666': '666',
    '670': '670',
    '674': '674',
    '678': '678',
    '682': '682',
    '686': '686',
    '688': '688',
    '690': '690',
    '694': '694',
    '702': '702',
    '704': '704',
    '706': '706',
    '710': '710',
    '716': '716',
    '729': '729',
    '740': '740',
    '748': '748',
    '756': '756',
    '760': '760',
    '762': '762',
    '764': '764',
    '768': '768',
    '772': '772',
    '776': '776',
    '780': '780',
    '784': '784',
    '788': '788',
    '792': '792',
    '795': '795',
    '796': '796',
    '798': '798',
    '800': '800',
    '804': '804',
    '807': '807',
    '818': '818',
    '831': '831',
    '832': '832',
    '833': '833',
    '834': '834',
    '840': '840',
    '850': '850',
    '854': '854',
    '858': '858',
    '860': '860',
    '862': '862',
    '876': '876',
    '882': '882',
    '887': '887',
    '894': '894',
    '900': '900',
} as const;
export type CONSORS_NATIONALITY_ISO_OTHERS_TYPE = ValueOf<typeof CONSORS_NATIONALITY_ISO_OTHERS>;

export const CONSORS_NOBILITY_TITLES = {
    BARON: 'BARON',
    BARONESS: 'BARONESS',
    BARONIN: 'BARONIN',
    FREIFRAU: 'FREIFRAU',
    FREIHERR: 'FREIHERR',
    FREIHERR_GRAF: 'FREIHERR_GRAF',
    FREIHERR_RITTER: 'FREIHERR_RITTER',
    FREIIN: 'FREIIN',
    FUERST: 'FUERST',
    GRAEFIN: 'GRAEFIN',
    GRAEFIN_FREIFRAU: 'GRAEFIN_FREIFRAU',
    GRAF: 'GRAF',
    HERZOG: 'HERZOG',
    MARKGRAF: 'MARKGRAF',
    PRINZ: 'PRINZ',
    PRINZESSIN: 'PRINZESSIN',
    RITTER: 'RITTER',
} as const;
export type CONSORS_NOBILITY_TITLE = ValueOf<typeof CONSORS_NOBILITY_TITLES>;

export const CONSORS_PROFESSIONS = {
    HOUSE_WIFE: 'HOUSE_WIFE',
    MANAGING_DIRECTOR: 'MANAGING_DIRECTOR',
    PENSIONER: 'PENSIONER',
    PUBLIC_SERVANT: 'PUBLIC_SERVANT',
    REGULAR_EMPLOYED: 'REGULAR_EMPLOYED',
    SELF_EMPLOYED: 'SELF_EMPLOYED',
    STUDENT: 'STUDENT',
    UNEMPLOYED: 'UNEMPLOYED',
    WORKER: 'WORKER',
} as const;
export type CONSORS_PROFESSION = ValueOf<typeof CONSORS_PROFESSIONS>;

export const CONSORS_PURPOSES_OF_LOAN = {
    AMA: 'AMA',
    ELK: 'ELK',
    ERM: 'ERM',
    FSH: 'FSH',
    GTW: 'GTW',
    GVA: 'GVA',
    PAZ: 'PAZ',
    RUE: 'RUE',
    RVA: 'RVA',
    UCK: 'UCK',
} as const;
export type CONSORS_PURPOSE_OF_LOAN = ValueOf<typeof CONSORS_PURPOSES_OF_LOAN>;

export const CONSORS_RESIDENCE_PERMIT_TYPES = {
    LIMITED_RESIDENCE_PERMIT_PARAGRAPH_19A: 'LIMITED_RESIDENCE_PERMIT_PARAGRAPH_19A',
    LIMITED_RESIDENCE_PERMIT_PARAGRAPH_OTHERS: 'LIMITED_RESIDENCE_PERMIT_PARAGRAPH_OTHERS',
    PERMANENT_RESIDENCE_PERMIT: 'PERMANENT_RESIDENCE_PERMIT',
    RESIDENCE_CARD: 'RESIDENCE_CARD',
    RESIDENCE_PERMIT_CARD: 'RESIDENCE_PERMIT_CARD',
    RESIDENCE_PERMIT_OTHER: 'RESIDENCE_PERMIT_OTHER',
    RESIDENCE_PERMIT_SWITZERLAND: 'RESIDENCE_PERMIT_SWITZERLAND',
    UNLIMITED_RESIDENCE_PERMIT: 'UNLIMITED_RESIDENCE_PERMIT',
} as const;
export type CONSORS_RESIDENCE_PERMIT_TYPE = ValueOf<typeof CONSORS_RESIDENCE_PERMIT_TYPES>;

export const CONSORS_SUBSCRIBERS = {
    BOTH: 'BOTH',
    MAIN: 'MAIN',
    PARTNER: 'PARTNER',
} as const;
export type CONSORS_SUBSCRIBER = ValueOf<typeof CONSORS_SUBSCRIBERS>;

export const CONSORS_DECISION_TYPES = {
    CONTRACT_ARRIVED: 'CONTRACT_ARRIVED',
    CONTRACT_RECEPTION: 'CONTRACT_RECEPTION',
    CONTRACT_REVIEW: 'CONTRACT_REVIEW',
    CREDIT_DECISION: 'CREDIT_DECISION',
    CREDIT_DISBURSMENT: 'CREDIT_DISBURSMENT',
} as const;
export type CONSORS_DECISION_TYPE = ValueOf<typeof CONSORS_DECISION_TYPES>;

export const CONSORS_DOWNLOAD_SUBSCRIPTION_DOCUMENT_REQUEST_STATUSES = {
    FAILED: 'FAILED',
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
} as const;
export type CONSORS_DOWNLOAD_SUBSCRIPTION_DOCUMENT_REQUEST_STATUS = ValueOf<
    typeof CONSORS_DOWNLOAD_SUBSCRIPTION_DOCUMENT_REQUEST_STATUSES
>;
export const CONSORS_DOWNLOAD_SUBSCRIPTION_DOCUMENT_REQUEST_FAILURE_REASONS = {
    DOCUMENT_NOT_YET_CREATED: 'DOCUMENT_NOT_YET_CREATED',
    DOWNLOAD_FAILED: 'DOWNLOAD_FAILED',
    DOWNLOAD_PENDING: 'DOWNLOAD_PENDING',
    NO_DOCUMENT_FOUND: 'NO_DOCUMENT_FOUND',
    UPLOAD_AWS_FAILED: 'UPLOAD_AWS_FAILED',
} as const;
export type CONSORS_DOWNLOAD_SUBSCRIPTION_DOCUMENT_REQUEST_FAILURE_REASON = ValueOf<
    typeof CONSORS_DOWNLOAD_SUBSCRIPTION_DOCUMENT_REQUEST_FAILURE_REASONS
>;
